export default defineAppConfig({
  ui: {
    // 顶栏
    header: {
      slots: {
        root: 'border-0 backdrop-blur-none lg:h-(--ui-header-height)',
      },
    },

    // 按钮
    // https://ui.nuxt.com/docs/components/button#theme
    button: {
      base: [
        'flex justify-center items-center',
        'hover:cursor-pointer',
        'text-nowrap',
        'rounded-none',
      ],
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class: 'hover:brightness-110 active:brightness-90 transition duration-300',
        },
      ],
      defaultVariants: {
        color: 'primary',
        variant: 'solid',
        size: 'md',
      },
    },

    // 提示气泡
    // https://ui.nuxt.com/docs/components/toast#theme
    toast: {
      slots: {
        root: 'ring-0 ring-gray-200 drop-shadow-lg rounded-none',
        title: 'flex items-center text-dark-200 lg:text-md text-lg font-bold',
        description: 'text-md font-semibold mt-0',
        progress: 'bg-black/20',
        icon: 'text-3xl my-auto p-1',
        close: 'p-1 rounded-none hover:bg-gray-300/40',
      },
    },

    // 卡片
    // https://ui.nuxt.com/docs/components/card#theme
    card: {
      slots: {
        root: ['rounded-none'],
      },
    },

    // 头像
    // https://ui.nuxt.com/docs/components/avatar#theme
    avatar: {
      slots: { root: ['hover:cursor-pointer'] },
    },

    // 链接
    // https://ui.nuxt.com/docs/components/link#theme
    link: {
      base: 'capitalize hover:cursor-pointer rounded-none',
      variants: {},
      compoundVariants: [
        {
          active: false,
          disabled: false,
          class: ['text-muted/80 hover:text-primary'],
        },
      ],
    },

    // 模态
    // https://ui.nuxt.com/docs/components/modal#theme
    modal: {
      slots: {
        overlay: 'bg-default/10 backdrop-blur-sm',
      },
      variants: {
        fullscreen: {
          false: {
            content: 'rounded-none',
          },
        },
      },
    },

    // 轮播图
    // https://ui.nuxt.com/docs/components/carousel#theme
    carousel: {
      slots: {
        container: 'flex items-center -ms-0',
        viewport: '',
        next: [
          'ring-0 justify-center bg-transparent aria-disabled:bg-transparent disabled:bg-transparent',
          'rounded-none',
        ],
        prev: [
          'ring-0 justify-center bg-transparent aria-disabled:bg-transparent disabled:bg-transparent',
          'rounded-none',
        ],
        dots: ['gap-3'],
        dot: ['h-2 lg:w-10 w-8', 'rounded-none', 'bg-default/40'],
      },
      variants: {
        orientation: {
          horizontal: {
            container: '-ms-0',
            item: 'ps-0',
          },
        },
      },
    },

    // 导航菜单
    // https://ui.nuxt.com/docs/components/navigation-menu#theme
    navigationMenu: {
      slots: {
        // 展开菜单大容器
        viewport: ['bg-default/90 backdrop-blur-sm', 'rounded-none'],
        link: ['uppercase'],
        // 展开后按钮文字标题
        childLinkLabel: ['capitalize'],
        // 展开后子项描述文字
        childLinkDescription: '',
      },
      variants: {
        orientation: {
          horizontal: {
            // 设置whitespace-nowrap让文字不可换行
            root: '',
            // 外部按钮组容器（gap控制按钮之间间距、my控制内外层容器间距）
            list: 'gap-x-4 my-4',
            // 外部单按钮父容器
            item: 'py-1 hover:bg-primary/10',
            // 外部单按钮
            link: ['px-4 py-1 cursor-pointer text-md', 'font-semibold'],
            // 行容器展开后内部单行按钮
            childList: ['py-2 px-0 w-full gap-x-20'],
            // 内部单按钮
            childItem: ['p-1', 'relative', 'hover:bg-primary/10', 'hover_underline_left_to_top'],
            // 内部按钮图标与文字的父容器
            childLink: ['items-center gap-4'],
            // 单按钮标题
            childLinkLabel: ['font-bold text-wrap'],
            // 所有内容大容器
            content: '',
          },
          vertical: {
            root: 'w-full overflow-y-auto touch-pan-y',
            // 单行按钮
            link: [
              'data-active:bg-primary/20',
              'data-[state=open]:bg-primary/10',
              'data-[state=open]:border-l-8 border-primary',
              'px-4 py-2',
              'text-lg font-semibold',
            ],
            childLinkLabel: ['font-semibold text-wrap'],
            // 单菜单容器
            item: ['border-b border-primary/40', 'data-[state=open]:bg-primary/10'],
            childList: 'ms-0 ps-0',
            childItem: 'ps-0',
          },
        },
      },
      compoundVariants: [
        // 与当前页面匹配的菜单行样式，也就是active状态下
        {
          orientation: 'vertical',
          active: true,
          class: {
            link: 'text-secondary',
            linkLeadingIcon: 'text-secondary',
          },
        },
        // 修复展开后子项左侧边线与文字之间的间距
        {
          orientation: 'vertical',
          collapsed: false,
          class: {
            childItem: 'ps-0',
          },
        },
        // 修复展开后子项容器左侧空白区域
        {
          orientation: 'vertical',
          collapsed: false,
          class: {
            childList: ['ms-0'],
            childItem: 'ps-0 -ms-0',
          },
        },
        // 优化左侧线条
        {
          orientation: 'vertical',
          highlight: true,
          level: true,
          class: {
            link: [
              // 隐藏展开后单行左边线
              'after:w-0',
              // 修复零间距时，左边线与文字的间距
              'after:-start-0',
              // 移除左边线圆角
              'after:rounded-none',
            ],
          },
        },
        // 优化文字颜色
        {
          orientation: 'vertical',
          active: false,
          class: {
            link: 'text-primary',
            linkLeadingIcon: 'text-primary',
          },
        },
        {
          orientation: 'horizontal',
          class: {
            // 覆盖外部按钮不必要的背景色
            link: 'before:bg-transparent hover:before:bg-transparent data-[state=open]:before:bg-transparent',
            // 覆盖内部按钮不必要的背景色
            childLink: ['hover:before:bg-transparent data-active:before:bg-transparent'],
          },
        },
      ],
    },

    // 表单字段容器
    // https://ui.nuxt.com/docs/components/form-field#theme
    formField: {
      slots: {},
    },

    // 输入框
    // https://ui.nuxt.com/docs/components/input#theme
    input: {
      slots: {
        root: 'w-full',
        base: ['rounded-none'],
      },
    },

    // 输入框菜单
    // https://ui.nuxt.com/docs/components/input-menu#theme
    inputMenu: {
      slots: {
        base: ['rounded-none'],
        content: ['rounded-none'],
      },
    },

    // 下拉菜单
    // https://ui.nuxt.com/docs/components/dropdown-menu#theme
    dropdownMenu: {
      slots: {
        content: ['min-w-20 w-40 ring-0 border border-gray-200 shadow-2xl', 'rounded-none'],
        group: 'px-0 py-2',
        itemLeadingIcon: 'mx-1',
        // 单行按钮
        item: 'py-10 px-2 text-sm cursor-pointer hover:bg-primary/10 hover_underline_left_to_top',
      },
    },

    // 下拉菜单选择
    // https://ui.nuxt.com/docs/components/select-menu#theme
    selectMenu: {
      slots: {
        base: ['hover:cursor-pointer', 'rounded-none'],
        content: ['rounded-none'],
        item: ['hover:text-primary hover:bg-primary/10 hover:cursor-pointer'],
      },
    },

    // 多行文本框
    // https://ui.nuxt.com/docs/components/textarea#theme
    textarea: {
      slots: {
        base: ['rounded-none'],
      },
    },

    // 切换按钮
    // https://ui.nuxt.com/docs/components/switch#theme
    switch: {
      slots: {
        base: '',
      },
    },

    // 手风琴列表
    // https://ui.nuxt.com/docs/components/accordion#theme
    accordion: {
      slots: {
        root: '',
        item: 'border-b-2 border-primary',
        // 标题行
        trigger: 'hover:cursor-pointer gap-2 text-shadow-sm',
        // 标题内容
        label: 'lg:text-xl text-lg text-white font-semibold',
        // 展开后文字
        body: 'lg:text-lg text-md text-white',
        // 前图标
        leadingIcon: 'size-6 text-white',
        // 后图标
        trailingIcon: 'size-6 text-white',
        header: '',
        content: '',
      },
    },

    // 步进器
    // https://ui.nuxt.com/docs/components/stepper#theme
    stepper: {
      slots: {
        root: 'gap-10',
        header: '',
        item: '',
        container: '',
        trigger: 'bg-gray-100',
        indicator: '',
        icon: '',
        separator: 'bg-gray-100',
        wrapper: '',
        title: '',
        description: '',
        content: '',
      },
    },

    // 悬浮提示
    // https://ui.nuxt.com/docs/components/tooltip#theme
    tooltip: {
      slots: {
        content: ['h-auto p-2', 'rounded-none'],
        arrow: '',
        text: 'truncate text-md',
        kbds: '',
        kbdsSize: '',
      },
    },

    // 日期选择器
    // https://ui.nuxt.com/docs/components/calendar#as-a-datepicker

    // 时间线
    // https://ui.nuxt.com/docs/components/timeline#theme
    timeline: {
      slots: {
        root: 'last:pb-0 ',
      },
    },

    // 上下篇文章
    // https://ui.nuxt.com/docs/components/content-surround#theme
    contentSurround: {
      slots: {
        root: '',
        link: ['bg-default/10 hover:bg-default/20'],
        linkLeading: [],
        linkLeadingIcon: [],
        linkTitle: 'text-lg font-bold',
        linkDescription: '',
      },
    },

    // 文章搜索弹窗
    // https://ui.nuxt.com/docs/components/content-search#theme
    contentSearch: {
      slots: {
        modal: ['bg-muted/80', 'rounded-none'],
        close: 'hover:bg-primary/20 lg:ring-0 lg:hover:ring ring ring-primary/40',
      },
    },

    // 文章内容标题导航
    // https://ui.nuxt.com/docs/components/content-toc#theme
    contentToc: {
      slots: {
        root: 'top-[calc(var(--ui-header-height)+var(--ui-crumb-height))] z-0 sm:py-0',
        container: 'py-0 justify-center',
        trigger: 'lg:text-2xl text-lg mt-0',
        link: 'lg:text-md text-sm',
      },
    },
  },
})
